import {
  HTTP_INTERCEPTORS,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { NgxSpinnerService } from 'ngx-spinner';
import { AlertService } from '@mavles/shared';
import { AuthSessionService } from '@mavles/user';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authSession = inject(AuthSessionService);
  private router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  private alert = inject(AlertService);

  private isLoggingOut = false;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.authSession.getToken();

    const authReq = token
      ? req.clone({
          setHeaders: {
            [TOKEN_HEADER_KEY]: `Bearer ${token}`,
          },
        })
      : req;

    return next.handle(authReq).pipe(
      catchError((err) => {
        // =========================================
        // 🔹 SESSION EXPIRED
        // =========================================
        if (err.status === 401 && !this.isLoggingOut) {
          this.isLoggingOut = true;

          this.authSession.clearSession();

          this.alert.error('Session expired');

          this.router.navigate(['/account/login']);
        }

        // =========================================
        // 🔹 VALIDATION ERRORS (422)
        // =========================================
        else if (err.status === 422) {
          const validationErrors = err?.error?.data;

          if (validationErrors) {
            Object.keys(validationErrors).forEach((field) => {
              const messages = validationErrors[field];

              if (Array.isArray(messages)) {
                messages.forEach((message: string) => {
                  this.alert.error(message);
                });
              }
            });
          } else {
            this.alert.error(err?.error?.message || 'Validation failed');
          }
        }

        // =========================================
        // 🔹 SERVER ERRORS
        // =========================================
        else if (err.status === 500 || err.status === 501) {
          this.alert.error('Application Error');
        }

        // =========================================
        // 🔹 OTHER ERRORS
        // =========================================
        else {
          const message =
            err?.error?.message || err?.message || 'Something went wrong';

          this.alert.error(message);
        }

        return throwError(() => err);
      }),

      finalize(() => {
        this.spinner.hide();
      }),
    );
  }
}

export const authInterceptorProviders = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
];


